import React, { useEffect, useRef, useState } from 'react';
import './SearchDiscover.css';

export const searchableCompanies = (rows, directory) => {
  const companies = new Map(directory.map((row) => [row.ticker, { ...row, uncovered: true }]));
  rows.forEach((row) => companies.set(row.ticker, row));
  return companies;
};

const CompanySearch = ({ companies, onSelect, id = 'discovery', label = 'Search companies', showSubmit = false, onSearch, query: controlledQuery, onQueryChange, statusContent = null }) => {
  const [localQuery, setLocalQuery] = useState('');
  const query = controlledQuery ?? localQuery;
  const setQuery = onQueryChange || setLocalQuery;
  const [activeResult, setActiveResult] = useState(0);
  const [focused, setFocused] = useState(false);
  const activeOption = useRef(null);
  const term = query.trim().toLowerCase();
  const terms = term.split(/\s+/).filter(Boolean);
  const visible = [...companies.values()].filter((row) => terms.every((part) =>
    `${row.ticker} ${row.company} ${row.group || ''} ${row.pitch || ''}`.toLowerCase().includes(part)
  )).sort((a, b) => {
    const rank = (row) => row.ticker.toLowerCase() === term ? 0
      : row.ticker.toLowerCase().startsWith(term) ? (row.uncovered ? 2 : 1) : row.uncovered ? 4 : 3;
    return rank(a) - rank(b) || a.ticker.length - b.ticker.length || a.ticker.localeCompare(b.ticker);
  }).slice(0, 12);
  const expanded = Boolean(term && (!showSubmit || focused));
  const activeTicker = visible[activeResult]?.ticker;
  const results = expanded && <div className="discovery-results" id={`${id}-results`} role="listbox" aria-label={showSubmit ? 'Header search results' : 'Search results'}>
    {visible.map((row, index) => <button type="button" role="option" aria-selected={index === activeResult}
      ref={index === activeResult ? activeOption : null}
      id={`${id}-result-${index}`} key={row.ticker} onClick={() => select(row.ticker)}>
      <strong>{row.ticker}</strong><span>{row.company}</span>{row.uncovered && <small>Not covered yet</small>}
    </button>)}
    {!visible.length && <p>No companies match your search.</p>}
  </div>;
  useEffect(() => {
    if (expanded) activeOption.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeResult, activeTicker, expanded]);
  const select = (ticker) => { setQuery(''); setActiveResult(0); onSelect(ticker); };
  return <form className="discovery-search" role={showSubmit ? 'search' : undefined}
    onFocus={() => setFocused(true)}
    onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
    onSubmit={(event) => {
      event.preventDefault();
      if (term && visible[activeResult]) select(visible[activeResult].ticker);
      else if (term && onSearch) onSearch(query.trim());
    }}>
    <label htmlFor={`${id}-query`}>{label}</label>
    <div className="discovery-search-field">
      <svg className="discovery-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
        <circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" />
      </svg>
      <input id={`${id}-query`} value={query} autoComplete="off" placeholder="Search by ticker or company name"
        role="combobox" aria-autocomplete="list" aria-expanded={Boolean(expanded && visible.length)}
        aria-controls={`${id}-results`} aria-activedescendant={expanded && visible[activeResult] ? `${id}-result-${activeResult}` : undefined}
        onChange={(event) => { setQuery(event.target.value); setActiveResult(0); }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setQuery('');
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveResult((current) => Math.max(0, Math.min(visible.length - 1, current + (event.key === 'ArrowDown' ? 1 : -1))));
          }
        }} />
      {showSubmit && <button className="header-search-submit" type="submit" aria-label="Search and discover" disabled={!term}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14m-6-6 6 6-6 6" /></svg>
      </button>}
    </div>
    {showSubmit ? <div className="header-search-dropdown">{statusContent}{results}</div> : results}
  </form>;
};

export default CompanySearch;
